/**
 * Generate Monarch Tokenizer from Syntax Definitions
 *
 * This script generates the Monarch tokenizer configuration
 * from the single source of truth in syntax-definitions.ts
 */

import { controlKeywords, measurementCommands, standardCommands, operators, chunkKeywords, getKeywordPattern } from './syntax-definitions.js';

/**
 * Generate Monarch tokenizer provider
 */
export function generateMonarchTokensProvider() {
    // Split commands into chunks for better readability
    const commandChunks = chunkKeywords(standardCommands, 7);

    return {
        // Control flow keywords
        controlKeywords: [...controlKeywords],

        // Measurement loop commands
        measurementCommands: [...measurementCommands],

        // Standard commands
        commands: [...standardCommands],

        // Conditional operators
        operators: [...operators],

        tokenizer: {
            root: [
                // Comments
                [/#.*$/, 'comment'],

                // endloop keyword - must come before general keywords to match measurement color
                [/\bendloop\b/, 'keyword.measurement'],

                // Control flow keywords (excluding endloop which is handled above)
                [new RegExp(getKeywordPattern(controlKeywords.filter((k) => k !== 'endloop'))), 'keyword'],

                // Measurement commands (split into chunks for readability)
                ...chunkKeywords(measurementCommands, 6).map((chunk) => [new RegExp(getKeywordPattern(chunk)), 'keyword.measurement'] as [RegExp, string]),

                // Standard commands (split into chunks for readability)
                ...commandChunks.map((chunk) => [new RegExp(getKeywordPattern(chunk)), 'keyword.command'] as [RegExp, string]),

                // VarType (two-letter identifiers)
                [/\b[a-z][a-z]\b/, 'type.identifier'],

                // Numbers with SI prefixes
                [/-?\d+[afpnumkMGTPE]/, 'number.si'],
                [/-?\d+i/, 'number.integer'],
                [/-?\d+\.\d+/, 'number.float'],
                [/-?\d+/, 'number'],

                // Strings
                [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-terminated string
                [/"/, 'string', '@stringLiteral'],
                [/f"/, 'string', '@fstring'],

                // Identifiers
                [/[a-zA-Z_][a-zA-Z0-9_]*/, 'identifier'],

                // Operators
                [/[=!<>]=|&&|\|\||[<>]/, 'operator'],

                // Delimiters
                [/[[\](),]/, 'delimiter'],

                // Whitespace
                [/[ \t\r\n]+/, 'white'],
            ],

            stringLiteral: [
                [/[^\\"]+/, 'string'],
                [/\\./, 'string.escape'],
                [/"/, 'string', '@pop'],
            ],

            fstring: [
                [/\{/, 'string.interpolation.delimiter', '@fstringInterpolation'],
                [/[^\\"{]+/, 'string'],
                [/\\./, 'string.escape'],
                [/"/, 'string', '@pop'],
            ],

            fstringInterpolation: [
                // Array indexing
                [/\[/, 'delimiter.bracket'],
                [/\]/, 'delimiter.bracket', '@pop'],
                // Numbers with SI prefixes inside interpolation
                [/-?\d+[afpnumkMGTPE]/, 'number.si'],
                [/-?\d+i/, 'number.integer'],
                [/-?\d+\.\d+/, 'number.float'],
                [/-?\d+/, 'number'],
                // Variables
                [/[a-zA-Z_][a-zA-Z0-9_]*/, 'identifier.interpolation'],
                // Close interpolation
                [/\}/, 'string.interpolation.delimiter', '@pop'],
            ],
        },
    };
}

export const monarchTokensProvider = generateMonarchTokensProvider();
